import { PiX } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { formatFileSize } from '../../../utils/whatsapp/media.util';
import type { FilePreviewProps } from '../../../types/whatsapp/file-preview.types';
import { FilePreviewThumbnail } from './file-preview-thumbnail';
import { FileUploadStatus } from './file-upload-status';

/** Compact attachment row that preserves upload feedback and removal controls. */
export const FilePreview = ({
  file,
  mediaType,
  onRemove,
  className = '',
  disabled = false,
  uploadStep = 'none',
  uploadError,
}: FilePreviewProps) => {
  const { t } = useI18n();
  return (
    <div
      className={`flex min-w-0 items-center gap-2 rounded-xl border border-neutral-200 bg-neutral-50 p-2 [@container(max-height:280px)]:p-1 ${className}`}
    >
      <FilePreviewThumbnail file={file} mediaType={mediaType} />
      <div className="flex min-w-0 flex-1 flex-col">
        <Tooltip
          interactive
          as="span"
          size="sm"
          contentClassName="w-full"
          className="max-w-[min(20rem,calc(100vw-2rem))] break-all"
          content={file.name}
        >
          <Button
            appearance="ghost"
            variant="neutral"
            size="2xs"
            className="!h-auto !min-h-0 w-full !justify-start !p-0"
            aria-label={file.name}
          >
            <Text as="span" size="small" weight="medium" className="truncate">
              {file.name}
            </Text>
          </Button>
        </Tooltip>
        <FileUploadStatus
          uploadStep={uploadStep}
          uploadError={uploadError}
          fileSize={formatFileSize(file.size)}
        />
      </div>
      <Button
        appearance="ghost"
        variant="neutral"
        size="lg"
        className="[@container(max-height:280px)]:!h-8"
        isIconOnly
        onClick={onRemove}
        disabled={disabled}
        aria-label={t('communications.attachment.remove')}
      >
        <PiX className="h-5 w-5" />
      </Button>
    </div>
  );
};
